// =============================================================
// serviceWorkerRegistration.js
// Patrick rule 2026-04-26: SW registration was DISABLED months ago
// (someone commented out the body, claiming "fix API authentication"),
// which broke PWA install on Android Chrome + offline caching everywhere.
// Now reactivated for the Vite build.
//
// Vite serves /service-worker.js from public/ folder at the root path —
// no PUBLIC_URL needed (that was CRA-specific).
// =============================================================

const isLocalhost = Boolean(
  typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
  )
);

export function register(config) {
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] not supported in this browser');
    return;
  }

  // Skip SW registration on localhost dev — Vite's HMR conflicts with SW caching.
  // For local SW testing, build prod-style and serve the build/ folder.
  if (isLocalhost) {
    console.log('[SW] skipped on localhost dev');
    return;
  }

  // v69.247 — AUTO-RELOAD when a new SW takes control. This is what guarantees
  // Patrick (and every user) gets new code without a manual hard-refresh. The
  // recurring "stale grouping / old code" bug was: an installed SW kept serving
  // old hashed chunks and the browser only re-checked the SW ~once per 24h.
  // Guard against reload loops with a module flag.
  let hlRefreshing = false;
  // Only auto-reload on a genuine UPDATE (a SW already controlled this page). On a
  // brand-new visitor's first install, clients.claim() also fires controllerchange —
  // we must NOT reload them (it'd be a jarring flash on first visit).
  const hadControllerAtLoad = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hlRefreshing || !hadControllerAtLoad) return;
    hlRefreshing = true;
    console.log('[SW] new service worker activated → reloading for fresh code');
    window.location.reload();
  });

  window.addEventListener('load', () => {
    const swUrl = '/service-worker.js';
    navigator.serviceWorker
      // updateViaCache:'none' → the browser NEVER uses the HTTP cache for the SW
      // script (or its imports) when checking for updates. Defeats the CloudFront /
      // browser 1-year cache on /service-worker.js that hid new versions for days.
      .register(swUrl, { updateViaCache: 'none' })
      .then((registration) => {
        console.log('[SW] registered:', registration.scope);

        // Proactively poll for a new SW: on first load, every 60s, and whenever the
        // PWA regains focus (standalone PWAs otherwise almost never re-check).
        const poll = () => { registration.update().catch(() => {}); };
        poll();
        setInterval(poll, 60 * 1000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') poll();
        });

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;
          installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                console.log('[SW] new content available → applying');
                // Tell the waiting SW to activate immediately. Its activation fires
                // controllerchange (above) which reloads the page once.
                if (registration.waiting) {
                  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                }
                if (config && config.onUpdate) config.onUpdate(registration);
              } else {
                console.log('[SW] content cached for offline use');
                if (config && config.onSuccess) config.onSuccess(registration);
              }
            }
          };
        };
      })
      .catch((error) => {
        console.error('[SW] registration failed:', error);
      });
  });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}
